// src/components/deduction/Deduction.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./deduction.css";
import { fetchKapatBalanceList, fetchSelectedKapats } from "../../services/deduction";
import { useCustomerRepo } from "../../repos/AppRepoProvider";

/* ---------------- helpers ---------------- */
function safeParseArray(v) {
    try {
        const s = typeof v === "string" ? v.trim() : v;
        const out = typeof s === "string" ? JSON.parse(s) : s;
        return Array.isArray(out) ? out : [];
    } catch {
        return [];
    }
}
function fmt2(x) {
    const n = Number(x || 0);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}
function toNum(v) {
    const n = Number(String(v ?? "").trim());
    return Number.isFinite(n) ? n : 0;
}
function moneyINR(x) {
    const n = Number(x || 0);
    const s = fmt2(Math.abs(n));
    return n < 0 ? `-${"₹"}${s}` : `${"₹"}${s}`;
}
function extractBeforeDash(s) {
    const str = String(s || "").trim();
    const idx = str.search(/[-–—]/);
    return (idx >= 0 ? str.slice(0, idx) : str).trim();
}
function isExcludedName(name) {
    const trimmed = String(name || "").trim();
    const beforeDash = extractBeforeDash(trimmed);

    // Android excludes: "खाद्य" and "एड्व्हांस"
    // Web: also exclude English labels if they appear
    const EX = ["खाद्य", "एड्व्हांस", "Foods", "Food", "Advance"];

    return EX.includes(trimmed) || EX.includes(beforeDash);
}

// cache keys (like Android)
function keySelectedJson(mainuser) {
    return `selected_kapats_json_${String(mainuser || "")}`;
}
function keySelectedETag(mainuser) {
    return `selected_kapats_etag_${String(mainuser || "")}`;
}
function keySelectedLabel(mainuser) {
    return `selected_kapattype_label_${String(mainuser || "")}`;
}

export default function Deduction({ mainuser }) {
    const customers = useCustomerRepo();

    // dropdown list
    const [kapatTypes, setKapatTypes] = useState([]); // [{sr, label}]
    const [kapattype, setKapattype] = useState("");  // label shown in UI
    const [typeLoading, setTypeLoading] = useState(false);

    // table
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [err, setErr] = useState("");

    // paging
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);

    // ---------------- load dropdown items (getselectedkapatmd.php) ----------------
    async function loadKapatTypes() {
        if (!mainuser) return;

        setTypeLoading(true);
        try {
            // 1) paint from cache immediately (fast)
            const cachedJson = localStorage.getItem(keySelectedJson(mainuser)) || "";
            if (cachedJson) {
                const cachedArr = safeParseArray(cachedJson);
                const parsed = cachedArr
                    .map((o) => ({
                        sr: Number(o?.sr || 0),
                        label: String(o?.namemarathi || "").trim(),
                        type: String(o?.type || "").trim(),
                    }))
                    .filter((x) => x.sr > 0 && x.label && !isExcludedName(x.label));

                if (parsed.length) setKapatTypes(parsed);
            }

            // 2) fetch from server with If-None-Match
            const etag = localStorage.getItem(keySelectedETag(mainuser)) || null;

            const resp = await fetchSelectedKapats(mainuser, { refresh: 1, etag });

            // 304 => use cache
            if (resp.status === 304) {
                // still ensure selected label exists
                const savedLabel = localStorage.getItem(keySelectedLabel(mainuser)) || "";
                if (!kapattype && savedLabel) setKapattype(savedLabel);
                return;
            }

            // 200 => parse + cache
            const arr = safeParseArray(resp.text);

            const parsed = arr
                .map((o) => ({
                    sr: Number(o?.sr || 0),
                    label: String(o?.namemarathi || "").trim(),
                    type: String(o?.type || "").trim(),
                }))
                .filter((x) => x.sr > 0 && x.label && !isExcludedName(x.label));

            setKapatTypes(parsed);

            localStorage.setItem(keySelectedJson(mainuser), JSON.stringify(arr));
            if (resp.etag) localStorage.setItem(keySelectedETag(mainuser), resp.etag);

            // set default selection (like Android: first item + remember)
            const savedLabel = localStorage.getItem(keySelectedLabel(mainuser)) || "";
            if (savedLabel && parsed.some((x) => x.label === savedLabel)) {
                setKapattype(savedLabel);
            } else if (!kapattype && parsed.length) {
                setKapattype(parsed[0].label);
                localStorage.setItem(keySelectedLabel(mainuser), parsed[0].label);
            }
        } finally {
            setTypeLoading(false);
        }
    }

    // set initial selected label from cache (like Android restore)
    useEffect(() => {
        if (!mainuser) return;
        const saved = localStorage.getItem(keySelectedLabel(mainuser)) || "";
        if (saved) setKapattype(saved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainuser]);

    // load dropdown list
    useEffect(() => {
        if (!mainuser) return;
        loadKapatTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainuser]);

    // ---------------- load balances table (getkapatyenelistmd.php) ----------------
    async function loadBalances() {
        if (!mainuser || !kapattype) return;

        setLoading(true);
        setErr("");

        try {
            // IMPORTANT: Android passes extractBeforeDash(kapattype)
            const kapatParam = extractBeforeDash(kapattype) || kapattype;

            const text = await fetchKapatBalanceList(mainuser, kapatParam);
            const arr = safeParseArray(text);

            const seen = new Set();
            const mapped = [];

            for (const o of arr) {
                const kno = parseInt(String(o?.kno ?? "0").trim(), 10) || 0;
                const cname = String(o?.cname ?? o?.name ?? "").trim();
                if (kno <= 0) continue;
                if (!cname || cname === "0") continue;

                const key = `${kno}|${cname}`;
                if (seen.has(key)) continue;
                seen.add(key);

                const bal = toNum(o?.current_balance ?? o?.balance ?? 0);

                // Phone: from CustomerRepo cache if available
                let mob = "";
                try {
                    mob = customers?.getMobByKno?.(String(kno)) || "";
                } catch {
                    mob = "";
                }

                mapped.push({
                    kno: String(kno),
                    cname,
                    mobno: mob,
                    total: bal,
                });
            }

            setRows(mapped);
        } catch (e) {
            console.error("deduction load error", e);
            setErr(e?.message || "Network Error");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!mainuser || !kapattype) return;
        loadBalances();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainuser, kapattype]);

    // search + totals
    const visibleRows = useMemo(() => {
        // ✅ keep negative and positive, remove only exact 0 (also handles "0.00")
        let out = rows.filter((r) => {
            const v = Number(r.total ?? 0);
            return Number.isFinite(v) && v !== 0;
        });

        const q = search.trim().toLowerCase();
        if (!q) return out;

        return out.filter(
            (r) =>
                String(r.kno || "").toLowerCase().includes(q) ||
                String(r.cname || "").toLowerCase().includes(q) ||
                String(r.mobno || "").toLowerCase().includes(q)
        );
    }, [rows, search]);



    const totalAmount = useMemo(() => {
        let t = 0;
        for (const r of visibleRows) t += Number(r.total || 0) || 0;
        return t;
    }, [visibleRows]);

    // pagination
    const totalEntries = visibleRows.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
    const safePage = Math.min(page, totalPages);

    const paged = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return visibleRows.slice(start, start + pageSize);
    }, [visibleRows, safePage, pageSize]);

    const showingFrom = totalEntries === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const showingTo = Math.min(safePage * pageSize, totalEntries);

    useEffect(() => {
        setPage(1);
    }, [kapattype, search, pageSize]);

    function onTypeChange(label) {
        setKapattype(label);
        localStorage.setItem(keySelectedLabel(mainuser), label);
    }

    function onAddTx() {
        alert("TODO: Add In Hand Advance Transaction dialog");
    }

    return (
        <div className="dedPage">
            <div className="dedTopBar">
                <div className="dedTitleLeft">
                    <span className="dedMiniIcon" aria-hidden="true">✂</span>
                    <span className="dedPageTitle">Deduction</span>
                </div>

                <button className="dedAddBtn" type="button" onClick={onAddTx}>
                    ＋ Add In Hand Advance Transaction
                </button>
            </div>

            <div className="dedFilters">
                <div className="dedFilterBlock">
                    <div className="dedFilterLabel">Deduction Type :</div>

                    <select
                        className="dedSelect"
                        value={kapattype}
                        onChange={(e) => onTypeChange(e.target.value)}
                        disabled={typeLoading}
                    >
                        {kapatTypes.length === 0 ? (
                            <option value="">
                                {typeLoading ? "Loading..." : "No deduction types"}
                            </option>
                        ) : (
                            kapatTypes.map((k) => (
                                <option key={String(k.sr)} value={k.label}>
                                    {k.label}
                                </option>
                            ))
                        )}
                    </select>
                </div>
            </div>

            <div className="dedCard">
                <div className="dedCardHead">
                    <div className="dedCardTitle">Deduction</div>

                    <div className="dedSearchBox">
                        <span className="dedSearchLabel">Search</span>
                        <input
                            className="dedSearchInput"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search"
                        />
                    </div>
                </div>

                {err ? <div className="dedMsg err">{err}</div> : null}

                <div className="dedTableWrap">
                    <table className="dedTable">
                        <thead>
                            <tr>
                                <th>Farmer Code</th>
                                <th>Farmer Name</th>
                                <th>Phone Number</th>
                                <th className="num">Total Amount</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="dedMsg">Loading...</td>
                                </tr>
                            ) : paged.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="dedMsg">No rows</td>
                                </tr>
                            ) : (
                                <>
                                    {paged.map((r) => (
                                        <tr key={`${r.kno}-${r.cname}`}>
                                            <td>{r.kno}</td>
                                            <td><b>{r.cname}</b></td>
                                            <td>{r.mobno || "-"}</td>
                                            <td className={`num ${Number(r.total) < 0 ? "neg" : ""}`}>
                                                {moneyINR(r.total)}
                                            </td>
                                        </tr>
                                    ))}

                                    <tr className="dedTotalRow">
                                        <td><b>Total Amount</b></td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td className={`num ${Number(totalAmount) < 0 ? "neg" : ""}`}>
                                            {moneyINR(totalAmount)}
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="dedFooter">
                    <div className="dedShowing">
                        Showing {showingFrom} to {showingTo} of {totalEntries} entries
                    </div>

                    <div className="dedPager">
                        <button type="button" className="dedPgBtn" onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
                        <button type="button" className="dedPgBtn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
                        <button type="button" className="dedPgNum active">{safePage}</button>
                        <button type="button" className="dedPgBtn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
                        <button type="button" className="dedPgBtn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>

                        <select className="dedPageSize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}
